import { Form, Formik, FormikErrors, FormikProps } from 'formik';
import { Locale } from '@synergy/models';
import { ScrollToFieldError } from '@synergy/components';
import { SubPageHandle, useFormikSubPage } from '@synergy/utils';
import { Theme } from '@mui/material';
import { forwardRef, useEffect, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { useParams } from 'react-router';

import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import { ProductDetailOverviewFormValues } from './models/product-detail-overview-form-values';
import { ProductDetailOverviewSectionBasic } from './components/product-detail-overview-section-basic';
import { ProductDetailOverviewSectionCategories } from './components/product-detail-overview-section-categories';
import { ProductDetailOverviewSectionDelivery } from './components/product-detail-overview-section-delivery';
import { ProductDetailOverviewSectionDescription } from './components/product-detail-overview-section-description';
import { ProductDetailOverviewSectionNotes } from './components/product-detail-overview-section-notes';
import { ProductDetailOverviewSectionOrder } from './components/product-detail-overview-section-order';
import { ProductDetailOverviewSectionSearchTerms } from './components/product-detail-overview-section-search';
import { ProductDetailOverviewSectionSettings } from './components/product-detail-overview-section-settings';
import { fetchProductDetail, updateProductDetail } from './store/product-detail-overview-actions';
import {
    productDetailOverviewBaselineSelector,
    productDetailOverviewFormDataSelector,
} from './store/product-detail-overview-selectors';
import { productDetailOverviewValidationSchema } from './product-detail-overview-validation-shema';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        margin: theme.spacing(6, theme.general.pageContentSpacing),
    },
    form: {
        '& > div:not(:first-child)': {
            marginBottom: theme.spacing(6),
        },
    },
}));

export interface ProductDetailOverviewProps {
    selectedLanguage: Locale;
}

export const ProductDetailOverview = forwardRef<SubPageHandle, ProductDetailOverviewProps>((props, ref) => {
    const dispatch = useThunkDispatch();
    const { id: productIdParam } = useParams<{ id: string }>();
    const classes = useStyles();

    const productDetail = useAppSelector(state => state.product.detail.overview.productDetail);
    const { formValues, errors, touched } = useAppSelector(productDetailOverviewFormDataSelector);
    // The product as the server has it — see PaloDirtyComparison.
    const baseline = useAppSelector(productDetailOverviewBaselineSelector);
    const formikRef = useRef<FormikProps<ProductDetailOverviewFormValues>>(null);

    useEffect(() => {
        if (!productDetail && productIdParam && +productIdParam) {
            dispatch(fetchProductDetail(+productIdParam));
        }
        // We ignore changes to productDetail - it won't change after initial load and
        // may trigger on product-reset, which is not desired
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, productIdParam]);

    // The mask checks itself as it mounts, while the article is still on its way
    // and every required field is empty. Once it arrives the check has to run
    // again — otherwise that first verdict is what the mask reports when it is
    // left, and the tab keeps a fault dot although it holds a complete article.
    useEffect(() => {
        if (!productDetail) {
            return undefined;
        }

        const check = window.setTimeout(() => void formikRef.current?.validateForm(), 0);

        return () => window.clearTimeout(check);
    }, [productDetail]);

    useFormikSubPage(
        ref,
        formikRef,
        (
            values: ProductDetailOverviewFormValues,
            isDirty: boolean,
            isValid: boolean,
            errs: FormikErrors<ProductDetailOverviewFormValues>
        ) => {
            dispatch(updateProductDetail(values, isDirty, isValid, errs));
        }
    );

    return (
        <div className={classes.container}>
            <Formik
                onSubmit={() => Promise.resolve()}
                initialValues={formValues}
                validationSchema={productDetailOverviewValidationSchema(props.selectedLanguage)}
                enableReinitialize={true}
                // The errors parked in the store were measured while the article
                // was still on its way from the server, when every required
                // field was empty. Without this the mask came back from another
                // tab claiming that name, number and supplier were missing while
                // showing all three.
                validateOnMount={true}
                initialErrors={errors}
                initialTouched={touched}
                innerRef={formikRef}
            >
                <Form className={classes.form}>
                    <PaloDirtyComparison baseline={baseline} />
                    <ScrollToFieldError />
                    <ProductDetailOverviewSectionSettings />
                    <ProductDetailOverviewSectionBasic selectedLanguage={props.selectedLanguage} />
                    <ProductDetailOverviewSectionOrder />
                    <ProductDetailOverviewSectionDelivery />
                    <ProductDetailOverviewSectionDescription selectedLanguage={props.selectedLanguage} />
                    <ProductDetailOverviewSectionNotes />
                    <ProductDetailOverviewSectionSearchTerms />
                    <ProductDetailOverviewSectionCategories />
                </Form>
            </Formik>
        </div>
    );
});
